import { useState, useCallback, useMemo } from 'react';

interface UseBulkSelectionOptions<T> {
  items: T[];
  getItemId: (item: T) => string | number;
  onSelectionChange?: (selectedItems: T[]) => void;
}

export function useBulkSelection<T>({
  items,
  getItemId,
  onSelectionChange,
}: UseBulkSelectionOptions<T>) {
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());

  // Get selected items
  const selectedItems = useMemo(() => {
    return items.filter(item => selectedIds.has(getItemId(item)));
  }, [items, selectedIds, getItemId]);

  // Selection state
  const isSelected = useCallback((item: T) => {
    return selectedIds.has(getItemId(item));
  }, [selectedIds, getItemId]);

  const isAllSelected = useMemo(() => {
    return items.length > 0 && items.every(item => selectedIds.has(getItemId(item)));
  }, [items, selectedIds, getItemId]);

  const isPartiallySelected = useMemo(() => {
    return selectedIds.size > 0 && !isAllSelected;
  }, [selectedIds.size, isAllSelected]);

  // Selection actions
  const toggleItem = useCallback((item: T) => {
    const id = getItemId(item);
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      
      // Notify of selection change
      if (onSelectionChange) {
        const newSelectedItems = items.filter(item => newSet.has(getItemId(item)));
        onSelectionChange(newSelectedItems);
      }
      
      return newSet;
    });
  }, [getItemId, items, onSelectionChange]);

  const selectItem = useCallback((item: T) => {
    const id = getItemId(item);
    setSelectedIds(prev => {
      if (prev.has(id)) return prev;
      
      const newSet = new Set(prev);
      newSet.add(id);
      
      if (onSelectionChange) {
        const newSelectedItems = items.filter(item => newSet.has(getItemId(item)));
        onSelectionChange(newSelectedItems);
      }
      
      return newSet;
    });
  }, [getItemId, items, onSelectionChange]);

  const deselectItem = useCallback((item: T) => {
    const id = getItemId(item);
    setSelectedIds(prev => {
      if (!prev.has(id)) return prev;
      
      const newSet = new Set(prev);
      newSet.delete(id);
      
      if (onSelectionChange) {
        const newSelectedItems = items.filter(item => newSet.has(getItemId(item)));
        onSelectionChange(newSelectedItems);
      }
      
      return newSet;
    });
  }, [getItemId, items, onSelectionChange]);

  const selectAll = useCallback(() => {
    const allIds = new Set(items.map(getItemId));
    setSelectedIds(allIds);
    
    if (onSelectionChange) {
      onSelectionChange(items);
    }
  }, [items, getItemId, onSelectionChange]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    
    if (onSelectionChange) {
      onSelectionChange([]);
    }
  }, [onSelectionChange]);

  const toggleAll = useCallback(() => {
    if (isAllSelected) {
      clearSelection();
    } else {
      selectAll();
    }
  }, [isAllSelected, clearSelection, selectAll]);

  // Bulk selection by criteria
  const selectByCriteria = useCallback((predicate: (item: T) => boolean) => {
    const matchingItems = items.filter(predicate);
    const matchingIds = new Set(matchingItems.map(getItemId));
    
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      matchingIds.forEach(id => newSet.add(id));
      
      if (onSelectionChange) {
        const newSelectedItems = items.filter(item => newSet.has(getItemId(item)));
        onSelectionChange(newSelectedItems);
      }
      
      return newSet;
    });
  }, [items, getItemId, onSelectionChange]);

  const deselectByCriteria = useCallback((predicate: (item: T) => boolean) => {
    const matchingItems = items.filter(predicate);
    const matchingIds = new Set(matchingItems.map(getItemId));
    
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      matchingIds.forEach(id => newSet.delete(id));
      
      if (onSelectionChange) {
        const newSelectedItems = items.filter(item => newSet.has(getItemId(item)));
        onSelectionChange(newSelectedItems);
      }
      
      return newSet;
    });
  }, [items, getItemId, onSelectionChange]);

  // Selection helpers
  const getSelectionStats = useCallback(() => {
    return {
      total: items.length,
      selected: selectedIds.size,
      percentage: items.length > 0 ? Math.round((selectedIds.size / items.length) * 100) : 0,
    };
  }, [items.length, selectedIds.size]);

  return {
    // State
    selectedItems,
    selectedIds,
    selectedCount: selectedIds.size,
    
    // Selection state
    isSelected,
    isAllSelected,
    isPartiallySelected,
    
    // Actions
    toggleItem,
    selectItem,
    deselectItem,
    selectAll,
    clearSelection,
    toggleAll,
    
    // Bulk actions
    selectByCriteria,
    deselectByCriteria,
    
    // Helpers
    getSelectionStats,
  };
}